import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Banner, Button, Field, inputClass } from "../../components/ui";
import { authClient, authErrorMessage } from "../../lib/auth-client";
import { queryKeys, useMe } from "../../lib/queries";
import { AuthLayout } from "./AuthLayout";

/** Only same-app paths are accepted as `next` (no open redirects). */
const safeNext = (value: string | null) => (value?.startsWith("/") && !value.startsWith("//") ? value : "/");

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (me.data) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { error: signInError } = await authClient.signIn.email({ email, password });
    setBusy(false);
    if (signInError) {
      setError(authErrorMessage(signInError));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    navigate(next, { replace: true });
  }

  return (
    <AuthLayout title="Iniciar sesión">
      {params.get("reset") === "1" && (
        <div className="mb-4">
          <Banner tone="success">Contraseña actualizada. Ya puedes entrar.</Banner>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <Field label="Correo electrónico">
          {(id) => (
            <input
              id={id}
              type="email"
              autoComplete="email"
              required
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label="Contraseña">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        <Button type="submit" loading={busy} className="w-full">
          Entrar
        </Button>
      </form>
      <div className="mt-4 flex flex-col gap-1 text-xs text-gray-600">
        <Link to="/signup" className="underline hover:text-gray-900">
          ¿Primera vez? Crea tu cuenta
        </Link>
        <Link to="/forgot-password" className="underline hover:text-gray-900">
          Olvidé mi contraseña
        </Link>
      </div>
    </AuthLayout>
  );
}
